"use client"

import { useState, useEffect } from "react"
import axios from "axios"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog"
import { Badge } from "../ui/badge"
import { Textarea } from "../ui/textarea"
import { Button } from "../ui/button"
import { CheckCircle, XCircle, Clock, X, Check, Calendar, Timer } from 'lucide-react'
import { toast } from "../hooks/use-toast"
import { format } from "date-fns"

interface LeaveReviewModalProps {
  isOpen: boolean
  onClose: () => void
  leaveRequest: any
  isViewOnly?: boolean
  onActionComplete?: (result: "approved" | "rejected", requestID: number, comment?: string) => void
}

export function LeaveReviewModal({ isOpen, onClose, leaveRequest, isViewOnly = false, onActionComplete }: LeaveReviewModalProps) {


  // Determine manager status - prefer explicit statusLabel from list, fallback to numeric mapping
  const statusLabel: "Approved" | "Rejected" | "Pending" | "Cancelled" = (() => {
    const label = (leaveRequest?.statusLabel || "").toString().toLowerCase()
    if (label) {
      if (label.includes("approve")) return "Approved"
      if (label.includes("reject")) return "Rejected"
      if (label.includes("cancel")) return "Cancelled"
    }
    const raw = leaveRequest?.rawStatus ?? leaveRequest?.status
    if (typeof raw === 'number') {
      // 5 = Pending Manager Approval
      // 6 = Pending HR Approval (means manager approved)
      // 7 = Rejected by Manager
      // 9 = Cancelled
      if (raw === 5) return "Pending"
      if (raw === 6) return "Approved"
      if (raw === 7) return "Rejected"
      if (raw === 9) return "Cancelled"
    } else if (typeof raw === 'string') {
      const s = raw.toLowerCase()
      if (s.includes("approve")) return "Approved"
      if (s.includes("reject")) return "Rejected"
      if (s.includes("cancel")) return "Cancelled"
    }
    return "Pending"
  })()

  const getApprovalBadge = (status: string) => {
    switch (status) {
      case "Approved":
        return (
          <Badge className="bg-green-100 text-green-700 flex items-center gap-1">
            <CheckCircle className="h-3 w-3" /> Approved
          </Badge>
        )
      case "Pending":
        return (
          <Badge className="bg-orange-100 text-orange-700 flex items-center gap-1">
            <Clock className="h-3 w-3" /> Pending
          </Badge>
        )
      case "Rejected":
        return (
          <Badge className="bg-red-100 text-red-700 flex items-center gap-1">
            <XCircle className="h-3 w-3" /> Rejected
          </Badge>
        )
      // default:
      //   return <Badge variant="outline">{status}</Badge>
    }
  }

  const [managerComment, setManagerComment] = useState("")
  const [submitting, setSubmitting] = useState(false)

  // Clear manager comment each time a new request is opened
  useEffect(() => {
    if (isOpen) {
      // For pending: empty; for approved/rejected: show stored decision comment (read-only)
      if (statusLabel === "Pending") {
        setManagerComment("")
      } else {
        const decidedComment = (leaveRequest?.comments || "").trim() || (isApproved ? "Approved" : isRejected ? "Rejected" : "")
        setManagerComment(decidedComment)
      }
    }
  }, [isOpen, leaveRequest?.requestID])

  const formatDate = (iso?: string): string => {
    if (!iso) return "-"
    const d = new Date(iso)
    if (isNaN(d.getTime())) return String(iso)
    const day = String(d.getDate()).padStart(2, '0')
    const month = d.toLocaleString('en-GB', { month: 'short' })
    const year = d.getFullYear()
    return `${day}-${month}-${year}`
  }

  const formatDateTime = (iso?: string): string => {
    if (!iso) return "-"
    const d = new Date(iso)
    if (isNaN(d.getTime())) return String(iso)
    const day = String(d.getDate()).padStart(2, '0')
    const month = d.toLocaleString('en-GB', { month: 'short' })
    const year = d.getFullYear()
    const hours = String(d.getHours()).padStart(2, '0')
    const minutes = String(d.getMinutes()).padStart(2, '0')
    return `${day}-${month}-${year} ${hours}:${minutes}`
  }

  const getDayName = (dateString: string | null | undefined) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return format(date, "EEEE");
    } catch {
      return "";
    }
  }

  const getDayTypeColor = (dayType: string) => {
    const lower = dayType?.toLowerCase() || "";
    if (lower === "full day") return "bg-blue-500";
    if (lower === "first-half" || lower === "firsthalf") return "bg-orange-500";
    if (lower === "second-half" || lower === "secondhalf") return "bg-orange-500";
    return "bg-blue-500";
  }

  const isPending = statusLabel?.toLowerCase() === "pending"
  const isRejected = statusLabel?.toLowerCase() === "rejected"
  const isApproved = statusLabel?.toLowerCase() === "approved"
  const isCancelled = statusLabel?.toLowerCase() === "cancelled"

  const getManagerName = (): string => {

    
    if (isApproved || isRejected) {
      const managerName = leaveRequest?.managerName || leaveRequest?.approvedByName || leaveRequest?.modifiedByName    
      if (managerName && typeof managerName === 'string' && managerName !== '0' && managerName.trim() !== '') {
       
        return managerName
      }      
    } 
    
    return "-"
  }

  const managerApprovedBy = getManagerName()
  const getManagerResponseDate = () => {
    // The API returns managerResponseOn (camelCase) field
    if (leaveRequest?.managerResponseOn) {
      return formatDateTime(leaveRequest.managerResponseOn)
    }
    if (leaveRequest?.modifiedOn) {
      return formatDateTime(leaveRequest.modifiedOn)
    }
    if (leaveRequest?.approvedOn) {
      return formatDateTime(leaveRequest.approvedOn)
    }
    if (leaveRequest?.rejectedOn) {
      return formatDateTime(leaveRequest.rejectedOn)
    }
    return "-"
  }

  const managerResponseOn = getManagerResponseDate()
  const managerDecisionComment = (leaveRequest?.comments || "").trim() || (isApproved ? "Approved" : isRejected ? "Rejected" : "")

  const processAction = async (response: "Approve" | "Reject") => {
    if (!leaveRequest?.requestID && !leaveRequest?.id) {
      onClose()
      return
    }
    setSubmitting(true)
    try {
      const payload = {
        requestID: leaveRequest.requestID || leaveRequest.id,
        response: response === "Approve" ? "Approved" : "Rejected",
        comment: managerComment || "",
        modUser: leaveRequest.modUser || 0,
      }
      await axios.post(process.env.REACT_APP_API_URL + "/api/Leave/ProcessManagerAction", payload, { withCredentials: true })

      onClose()

      setTimeout(() => {
        if (response === "Approve") {
          toast({
            title: "Leave Approved",
            description: "",
            duration: 3000,
            className: "border-green-500 text-green-800",
          })
          onActionComplete && onActionComplete("approved", payload.requestID, managerComment || "")
        } else {
          toast({
            title: "Leave Rejected",
            description: "",
            duration: 3000,
            className: "border-red-500 text-red-800",
          })
          onActionComplete && onActionComplete("rejected", payload.requestID, managerComment || "")
        }
      }, 100)
    } catch (e) {
      toast({
        title: "Error",
        description: "Failed to process leave request. Please try again.",
        duration: 3000,
        className: "border-red-500 text-red-800"
      })
    } finally {
      setManagerComment("")
      setSubmitting(false)
    }
  }

  const handleApprove = () => processAction("Approve")
  const handleReject = () => processAction("Reject")

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-7xl max-h-[90vh] overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg">
        {/* Loading Overlay */}
        {submitting && (
          <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 flex flex-col items-center gap-3 shadow-xl">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
              <p className="text-sm font-medium text-gray-700">Processing...</p>
            </div>
          </div>
        )}
        
        {/* Fixed Header */}
        <DialogHeader className="border-b border-gray-200 pb-3 px-4 pt-4 flex flex-row items-center justify-between">
          <div className="flex items-center gap-3">
            <DialogTitle className="text-lg font-bold text-gray-900">
              {leaveRequest?.requestBy || leaveRequest?.requestedBy || "Employee"}
            </DialogTitle>
            <Badge className="text-orange-600 border-orange-200 bg-orange-50 text-xs font-medium">
              {leaveRequest?.leaveType || leaveRequest?.leaveName || "Leave"}
            </Badge>
          </div>
          {/* Applied On in header */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-blue-600" />
            <div className="flex flex-col items-end">
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Applied On</span>
              <span className="text-lg font-semibold text-gray-900">
                {leaveRequest?.appliedOn || leaveRequest?.requestDate || "--"}
              </span>
            </div>
          </div>
        </DialogHeader>

        {/* Scrollable Content Area */}
        <div className="space-y-4 px-4">
          {/* Status chip for Cancelled */}
          {statusLabel === "Cancelled" && (
            <div className="border-l-4 border-yellow-500 bg-yellow-50 p-3 rounded-r-lg">
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-yellow-600" />
                <p className="font-medium text-gray-900 text-sm">
                  Cancelled by Employee
                </p>
              </div>
            </div>
          )}

          {/* Leave Day Details at top with individual day cards */}
          {leaveRequest?.leaveDayDetails && Array.isArray(leaveRequest.leaveDayDetails) && leaveRequest.leaveDayDetails.length > 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="16" y1="2" x2="16" y2="6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="8" y1="2" x2="8" y2="6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="3" y1="10" x2="21" y2="10" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <h3 className="font-semibold text-gray-900 text-base">Leave Day Details</h3>
              </div>
              <div className="flex flex-wrap gap-16">
                {leaveRequest.leaveDayDetails.map((dayDetail: any, index: number) => {
                  const formatDayType = (dayType: string) => {
                    if (!dayType) return "Full Day";
                    const lower = dayType.toLowerCase();
                    if (lower === "first-half" || lower === "firsthalf") return "First-Half";
                    if (lower === "second-half" || lower === "secondhalf") return "Second-Half";
                    return "Full Day";
                  };
                  const formatDateDisplay = (dateString: string | null | undefined) => {
                    if (!dateString) return "--";
                    try {
                      const date = new Date(dateString);
                      if (isNaN(date.getTime())) return String(dateString);
                      return format(date, "dd-MM-yyyy");
                    } catch {
                      return "--";
                    }
                  };
                  const dayTypeColor = getDayTypeColor(dayDetail.dayType);
                  const dayTypeBadge = formatDayType(dayDetail.dayType);
                  const dayName = getDayName(dayDetail.leaveDate);
                  
                  return (
                    <div key={index} className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${dayTypeColor}`}></div>
                      <div className="flex flex-col">
                        <span className="text-base font-semibold text-gray-900">
                          {formatDateDisplay(dayDetail.leaveDate)}
                        </span>
                        <span className="text-xs text-gray-500">{dayName}</span>
                      </div>
                      <Badge className={`${
                        dayTypeBadge === "Full Day" 
                          ? "bg-blue-50 text-blue-700 border-blue-200" 
                          : "bg-orange-50 text-orange-600 border-orange-200"
                      } font-medium text-xs`}>
                        {dayTypeBadge}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}

          {/* Reason for Leave and Duration side by side */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Reason for Leave */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
                <h3 className="font-semibold text-gray-900 text-base">Reason for Leave</h3>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed">
                {leaveRequest?.reason || <span className="italic text-gray-400">No reason provided</span>}
              </p>
            </div>

            {/* Duration */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center">
                  <Timer className="w-4 h-4 text-purple-600" />
                </div>
                <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">DURATION</span>
              </div>
              <p className="text-2xl font-bold text-gray-900 mt-2">
                {leaveRequest?.totalDays || "1"} Day(s)
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {leaveRequest?.fromDate || leaveRequest?.leaveStartDate || "-"} - {leaveRequest?.toDate || leaveRequest?.leaveEndDate || "-"}
              </p>
            </div>
          </div>

          {/* Manager Approval Summary */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-gray-900 text-base">Manager Approval</h4>
              {getApprovalBadge(statusLabel)}
            </div>
            {(isApproved || isRejected) && (
              <div className="space-y-2 text-sm">
                <div className="flex items-start">
                  <span className="font-medium text-gray-500 w-24">Reviewer</span>
                  <span className="text-gray-900 flex-1">
                    {String(managerApprovedBy)}
                  </span>
                </div>
                <div className="flex items-start">
                  <span className="font-medium text-gray-500 w-24">Reviewed On</span>
                  <span className="text-gray-900 flex-1">
                    {managerResponseOn}
                  </span>
                </div>
                {managerDecisionComment && managerDecisionComment !== '-' && (
                  <div className="pt-2 border-t border-gray-200">
                    <span className="font-medium text-gray-500 block mb-1">Comment</span>
                    <p className="text-gray-700 break-all whitespace-pre-wrap text-sm">
                      {managerDecisionComment}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Manager comment input - show only when pending and not view-only */}
          {!isViewOnly && isPending && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Comment (optional)
                </label>
                <span className="text-xs text-gray-500">
                  {managerComment.length}/1000
                </span>
              </div>
              <Textarea
                value={managerComment}
                onChange={(e) => setManagerComment(e.target.value)}
                placeholder="Add a comment for your decision"
                className="min-h-[100px] bg-white border-gray-200 text-gray-900"
                maxLength={1000}
              />
            </div>
          )}
        </div>

        {/* Fixed Buttons */}
        <div className="border-t border-gray-200 px-4 py-4 flex justify-end gap-3 bg-white">
          {isViewOnly ? (
            <Button
              onClick={onClose}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2.5 font-medium rounded-md shadow-sm"
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={submitting || (statusLabel !== "Pending")}
                className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 font-medium rounded-md shadow-sm"
              >
                <X className="h-4 w-4" />
                Reject
              </Button>
              <Button
                onClick={handleApprove}
                disabled={submitting || (statusLabel !== "Pending")}
                className="flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-2.5 font-medium rounded-md shadow-sm"
              >
                <Check className="h-4 w-4" />
                Approve
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}